import { useEffect, useState } from "react";
import { Eye, Users, Clock, MapPin, MessageCircle, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle } from "@/components/admin/kit";

const sel = "h-10 rounded-full border bg-white px-4 text-sm";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-3xl bg-white border p-5" data-testid={testid}>
      <Icon className="w-5 h-5 text-slate-400" />
      <p className="text-3xl font-semibold mt-3 tabular-nums">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function Top({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid={testid}>
      <p className="font-semibold mb-4">{title}</p>
      {rows.length === 0 && <p className="text-sm text-slate-400">Nothing yet.</p>}
      <div className="space-y-3">{rows.map((r) => (
        <div key={r.name}><div className="flex justify-between text-sm"><span className="truncate pr-2">{r.name}</span><span className="font-semibold tabular-nums">{r.n}</span></div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full bg-slate-900" style={{ width: `${(r.n / max) * 100}%` }} /></div></div>
      ))}</div>
    </div>
  );
}

export default function TdvAnalytics() {
  const [days, setDays] = useState(30);
  const [tour, setTour] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/tdv/analytics", { params: { days, tour_id: tour } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [days, tour]);
  const t = d?.totals;
  return (
    <div data-testid="tdv-analytics">
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex-1"><PageTitle title="Analytics" sub="Who visited your tours and what they looked at." /></div>
        <select className={sel} value={tour} onChange={(e) => setTour(e.target.value)} data-testid="tdv-analytics-tour"><option value="">All tours</option>{(d?.tours || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
        <select className={sel} value={days} onChange={(e) => setDays(+e.target.value)} data-testid="tdv-analytics-days">{[7, 30, 90, 365].map((n) => <option key={n} value={n}>Last {n} days</option>)}</select>
      </div>
      {t && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
            <Stat icon={Eye} label="Tour views" value={t.views} testid="tdv-stat-views" />
            <Stat icon={Users} label="Visitors" value={t.visitors} testid="tdv-stat-visitors" />
            <Stat icon={Clock} label="Avg minutes per visitor" value={t.avg_minutes} testid="tdv-stat-minutes" />
            <Stat icon={MapPin} label="Hotspot clicks" value={t.hotspots} testid="tdv-stat-hotspots" />
            <Stat icon={MessageCircle} label="AI questions" value={t.questions} testid="tdv-stat-questions" />
            <Stat icon={CalendarDays} label="Viewing requests" value={t.leads} testid="tdv-stat-leads" />
          </div>
          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            <Top title="Most viewed scenes" rows={d.scenes} testid="tdv-top-scenes" />
            <Top title="Most clicked hotspots" rows={d.hotspots} testid="tdv-top-hotspots" />
            <Top title={`Top menu clicks (${t.menu})`} rows={d.menus} testid="tdv-top-menus" />
          </div>
          <div className="rounded-3xl bg-white border overflow-x-auto">
            <table className="w-full text-sm" data-testid="tdv-people-table">
              <thead><tr className="text-left text-slate-500 border-b"><th className="px-5 py-3 font-medium">Visitor</th><th className="px-5 py-3 font-medium text-right">Visits</th><th className="px-5 py-3 font-medium text-right">Minutes</th><th className="px-5 py-3 font-medium text-right">Scenes</th><th className="px-5 py-3 font-medium text-right">Hotspots</th><th className="px-5 py-3 font-medium text-right">Requests</th></tr></thead>
              <tbody>{d.people.map((p, i) => (
                <tr key={p.who} className="border-b last:border-0" data-testid={`tdv-person-${i}`}>
                  <td className="px-5 py-3">{p.named ? p.who : <span className="text-slate-500">{p.who}</span>}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{p.visits}</td><td className="px-5 py-3 text-right tabular-nums">{p.minutes}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{p.scenes}</td><td className="px-5 py-3 text-right tabular-nums">{p.hotspots}</td><td className="px-5 py-3 text-right tabular-nums">{p.leads}</td>
                </tr>
              ))}</tbody>
            </table>
            {d.people.length === 0 && <p className="text-sm text-slate-400 p-5">No visits yet. Share a tour link to start seeing activity.</p>}
          </div>
        </>
      )}
    </div>
  );
}
